import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { FileText, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function ContractSign() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [signerName, setSignerName] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/contracts/${token}`).then((r) => setContract(r.data)).catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || "Contrato não encontrado")).finally(() => setLoading(false));
  }, [token]);

  const sign = async () => {
    if (!accepted || signerName.trim().length < 3) { toast.error("Marque a aceitação e informe seu nome"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/contracts/${token}/sign`, { signer_name: signerName.trim(), accepted: true });
      setContract(r.data);
      toast.success("Contrato assinado!");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha ao assinar"); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="p-12 text-center text-stone-500"><Loader2 className="animate-spin mx-auto mb-2"/>Carregando contrato...</div>;
  if (err) return (
    <div className="max-w-2xl mx-auto px-4 py-12">
      <Card className="p-6 border-red-300 bg-red-50 rounded-2xl text-center" data-testid="contract-error">
        <AlertTriangle className="mx-auto text-red-600 mb-2" size={36}/>
        <h2 className="font-display text-xl font-bold text-red-900">{err}</h2>
        <p className="text-sm text-stone-600 mt-2">Verifique o link recebido por email ou entre em contato com o suporte.</p>
      </Card>
    </div>
  );

  const signed = contract?.status === "signed";

  return (
    <div className="max-w-3xl mx-auto px-4 py-8" data-testid="contract-sign-page">
      <Card className="p-6 rounded-2xl border-blue-200 mb-5 bg-blue-50">
        <div className="flex items-center gap-2 text-blue-900"><FileText size={22}/><span className="font-display text-xl font-bold">Termo de Contrato</span></div>
        <div className="mt-2 text-sm text-stone-700">
          <div><strong>Empresa:</strong> {contract.company_name}</div>
          <div><strong>Plano:</strong> {contract.plan_display_name} ({contract.plan_tier})</div>
          <div><strong>Emitido em:</strong> {new Date(contract.issued_at).toLocaleString("pt-BR")}</div>
          {signed && <div className="mt-2 text-emerald-700 font-bold flex items-center gap-1"><CheckCircle2 size={16}/>Assinado em {new Date(contract.signed_at).toLocaleString("pt-BR")} por {contract.signer_name}</div>}
        </div>
      </Card>

      <Card className="p-6 rounded-2xl border-stone-200 mb-5">
        <pre className="whitespace-pre-wrap font-sans text-sm text-stone-800 leading-relaxed" data-testid="contract-terms">{contract.terms}</pre>
      </Card>

      {!signed && (
        <Card className="p-6 rounded-2xl border-amber-300 bg-amber-50 space-y-4">
          <div><Label className="font-bold">Seu nome completo (assinatura digital)</Label><Input value={signerName} onChange={(e) => setSignerName(e.target.value)} placeholder="Como assina nos documentos oficiais" className="rounded-xl mt-1" data-testid="contract-signer-name"/></div>
          <label className="flex items-start gap-2 cursor-pointer">
            <Checkbox checked={accepted} onCheckedChange={(v) => setAccepted(!!v)} className="mt-0.5" data-testid="contract-accept"/>
            <span className="text-sm text-stone-800">Li integralmente e <strong>aceito</strong> os termos do contrato acima, em nome da empresa <strong>{contract.company_name}</strong>.</span>
          </label>
          <Button onClick={sign} disabled={busy || !accepted || signerName.trim().length < 3} className="w-full bg-blue-900 hover:bg-blue-950 text-white rounded-xl h-12 text-base" data-testid="contract-sign-btn">
            {busy ? "Assinando..." : "Assinar eletronicamente"}
          </Button>
        </Card>
      )}

      {signed && (
        <div className="text-center">
          <Button onClick={() => navigate("/merchant")} className="bg-blue-900 hover:bg-blue-950 text-white rounded-xl">Ir para meu painel</Button>
        </div>
      )}
    </div>
  );
}
